import { getRegions } from "@qgiv/core-js";
// Should these be passed in instead?
import {
    existingRegistrations,
    classifications,
    teams,
    categories,
    p2pSettings,
} from "../initialState";

// -------------------------------------------------------------------------
// TODO: This file needs to cleaned up once the product is working again.
// -------------------------------------------------------------------------

const existingPrimaryRegistration = existingRegistrations[0];
const existingPrimaryRegistrationKeys = Object.keys(
    existingPrimaryRegistration,
);

// NOTE - Should `existingRegistrations` contain any data,
//        we need to manipulate the starting data for classification and
//        registration details forms.

// Set the state data if there are existing registrations
export const getStateForExistingRegistration = (registration) => {
    // Region data is loaded into the form as State. Because Legacy P2P forms
    // store the full state name and the shortcode is required in order to
    // prefill the new P2P registration, a matching region object is located
    // and its shortcode is retrieved.

    if (registration.State && registration.Country) {
        getRegions(registration.Country).then((res) => {
            const regions = res.data;
            const rightRegion =
                regions.find(
                    (region) => region.stateName === registration.State,
                ) || "";
            return rightRegion !== ""
                ? { State: rightRegion.shortCode }
                : { State: "" };
        });
    }
};

/**
 * @public
 * @function getClassificationForExistingRegistration
 * @description sets registration Classification based on existing registration data
 * @returns {Array} array of classification id
 */
export const getClassificationForExistingRegistration = () => {
    // -------------------------------------------------------------------------
    // TODO: Add logic to set Classification if existing data from Team needs to
    // be limited to a Classification
    // -------------------------------------------------------------------------

    const numberOfExistingRegistrations = existingRegistrations.length;
    // Only classifications with limits risk not having enough spots left.
    // Classifications with no limits have a registrantSpotsLeft value of -1.
    const classificationsWithEnoughSpotsLeft = classifications.filter(
        ({ registrantSpotsLeft = -1 }) => {
            const hasUnlimitedSpots = registrantSpotsLeft === -1;
            let hasEnoughSpotsLeft = true;
            if (!hasUnlimitedSpots) {
                if (numberOfExistingRegistrations > registrantSpotsLeft) {
                    hasEnoughSpotsLeft = false;
                }
            }
            return hasEnoughSpotsLeft;
        },
    );
    // Evaluate whether spots are still available in the classification
    // associated with the existing primary registration. All id values should
    // be converted to the same type before performing the evaluation as the
    // type of these values is not always consistent.
    const classificationIdsWithEnoughSpotsLeft =
        classificationsWithEnoughSpotsLeft.map((classification) =>
            String(classification.id),
        );
    const hasClassification =
        existingPrimaryRegistration.Classification.length > 0;

    const classificationId = hasClassification
        ? String(existingPrimaryRegistration.Classification[0])
        : "";
    const hasEnoughSpotsLeft =
        classificationIdsWithEnoughSpotsLeft.includes(classificationId);
    // If the classification has sold out, do not set the classification as the
    // user will need to select a classification during registration.
    return hasEnoughSpotsLeft ? { Classification: [classificationId] } : {};
};

// NOTE - Should `existingRegistrations` contain any data,
//        we need to manipulate the starting data for classification and
//        registration details forms.

// Detect initial state of classification form
export const getClassificationInitialState = () =>
    // -------------------------------------------------------------------------
    // TODO: Add existingPrimaryRegistrationKeys.includes("Team") to set Classification
    // when attached to a team in existing Reg
    // -------------------------------------------------------------------------
    existingPrimaryRegistrationKeys.includes("Classification")
        ? getClassificationForExistingRegistration(
              existingRegistrations,
              existingPrimaryRegistration,
          )
        : {};

const classification = getClassificationInitialState();

const classificationId =
    Object.keys(classification).length > 0 &&
    classification.Classification &&
    classification.Classification.length > 0
        ? Number(classification.Classification[0])
        : "";

// Check for existing team and team does not exceed limits
// and team is valid on pre-selected classification:
export const existingTeamExistsOnCurrentTeams = (Team = "") => {
    const currentTeams = teams;
    const { limitTeamJoiningToClassification } = p2pSettings;

    if (Team) {
        const teamExists = currentTeams
            .map((team) => String(team.id))
            .includes(String(Team));
        if (teamExists) {
            const selectedTeam = currentTeams.find(
                (team) => String(team.id) === String(Team),
            );

            const teamAvailableOnClassification =
                limitTeamJoiningToClassification === false ||
                (limitTeamJoiningToClassification === true &&
                    selectedTeam.classification === classificationId);

            // teams that do not have a limit will not have the 'remainingSpots' key
            // so a default of -1 is assigned here
            const { remainingSpots = -1 } = selectedTeam;
            const totalExistingRegistrationsForThisTeam =
                existingRegistrations.filter(
                    (registration) => registration.Team === Team,
                ).length;

            const hasEnoughSpotsLeft =
                remainingSpots === -1 ||
                remainingSpots >= totalExistingRegistrationsForThisTeam;

            return !!(hasEnoughSpotsLeft && teamAvailableOnClassification);
        }
        return false;
    }
    return false;
};

// Category exists and that category's `attachedClassifications` array
// is empty or includes the preselected classification:
export const existingCategoryOnCurrentCategories = (Category = []) => {
    const currentCategories = categories;

    if (
        Category?.length > 0 &&
        currentCategories?.filter(
            (category) => String(category.id) === String(Category[0]),
        )?.length > 0
    ) {
        const preselectedCategory = currentCategories.find(
            (category) => String(category.id) === String(Category[0]),
        );
        const { attachedClassifications = [] } = preselectedCategory;

        return (
            attachedClassifications.length === 0 ||
            (classificationId &&
                attachedClassifications.includes(classificationId))
        );
    }
    return false;
};

export const getDataForManagedExistingRegistrations = (registration) => {
    if (registration.Managed_By_Primary) {
        return {
            Email: "",
        };
    }
    return {};
};

/**
 * @public
 * @function getRegistrationsInitialState
 * @description sets registrations based on existing registration data
 * @returns {Array} array of registrations
 */
export const getRegistrationsInitialState = () => {
    const registrationsInitialState =
        existingPrimaryRegistrationKeys.length > 0
            ? [
                  ...existingRegistrations.map((registration) => {
                      // NOTE - Filter out and refine any data provided by the
                      //        backend
                      const filteredRegistration = {
                          ...getStateForExistingRegistration(registration),
                          ...getDataForManagedExistingRegistrations(
                              registration,
                          ),
                      };

                      return {
                          ...registration,
                          ...filteredRegistration,
                          // If values for Category or Team do not exist/are not valid on current form's data
                          // unset them.
                          Category: existingCategoryOnCurrentCategories(
                              registration.Category,
                          )
                              ? registration.Category
                              : [],
                          Team: existingTeamExistsOnCurrentTeams(
                              registration.Team,
                          )
                              ? registration.Team
                              : "",
                          // NOTE - safe to assume email is valid if received in
                          //        `existingRegistrations`
                          Verified_Email_Is_Unused_By_Current_Registration: true,
                          Verified_Email_Is_Unused_By_Previous_Registration: true,
                          // 'prepareValuesForP2PRegistrationSubmit' did not honor
                          // the Team_Captain value unless the team ID was 'new-team'.
                          // Flag that this data came from existingRegistrations.
                          // Used for setting team captain value for INVITED participants
                          // PENDING registrations' team data is not carried over.
                          Existing_Registration: true,
                          // Flag which displays "Review Details" in summary and
                          // blocks form submission if any registrations have
                          // flag as false
                          Needs_To_Be_Reviewed: true,
                          Has_Been_Saved: true,
                          // Flag to determine if the registrant is using primary values, false by default
                          Use_Primary_Address: false,
                          Use_Primary_Phone: false,
                      };
                  }),
              ]
            : [{}];

    return registrationsInitialState;
};
